import React, { useMemo, useState } from 'react';
import { doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import AppShell from '../layout/AppShell';
import { useAuth } from '../../hooks/useAuth';
import { getFirebaseDb } from '../../services/firebaseConfig';
import './pages.css';

type BundleDocument = {
  collection: string;
  id: string;
  data: Record<string, unknown>;
};

type ImportBundle = {
  assessmentId: string;
  version: string;
  documents: BundleDocument[];
};

type SchemaQuestion = {
  id: string;
  type: string;
  text: string;
  helpText?: string;
  required?: boolean;
  hasCommentField?: boolean;
  commentPrompt?: string;
  polarityValidated?: boolean;
  polarityFixed?: boolean;
  answerMeaning?: {
    yes?: string;
    no?: string;
  };
  manualReview?: boolean;
  manualReviewReason?: string;
  tags?: string[];
  severityLevel?: 'low' | 'medium' | 'high' | 'critical';
  severityWeight?: number;
};

type SchemaCategory = {
  id: string;
  title: string;
  questions: SchemaQuestion[];
};

type AssessmentSchema = {
  id: string;
  version: string;
  metadata: {
    title: string;
    description: string;
    lastUpdated: string;
    estimatedDuration?: number;
    totalQuestions?: number;
    categories?: number;
  };
  categories: SchemaCategory[];
};

function inferSeverity(question: SchemaQuestion): 'low' | 'medium' | 'high' | 'critical' {
  if (question.severityLevel) return question.severityLevel;
  if (question.manualReview) return 'high';
  return question.type === 'boolean' ? 'medium' : 'low';
}

function severityWeight(level: 'low' | 'medium' | 'high' | 'critical'): number {
  switch (level) {
    case 'critical':
      return 10;
    case 'high':
      return 6;
    case 'low':
      return 2;
    case 'medium':
    default:
      return 4;
  }
}

function sectionName(question: SchemaQuestion): string {
  return (question.helpText || 'Section: General').replace(/^Section:\s*/, '').trim() || 'General';
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function buildBundleFromSchema(schema: AssessmentSchema): ImportBundle {
  const assessmentId = schema.id;
  const version = schema.version;
  const documents: BundleDocument[] = [
    {
      collection: 'assessments',
      id: assessmentId,
      data: {
        title: schema.metadata.title,
        description: schema.metadata.description,
        version,
        lastUpdated: schema.metadata.lastUpdated,
        estimatedDuration: schema.metadata.estimatedDuration,
        totalQuestions:
          schema.metadata.totalQuestions ||
          schema.categories.reduce((sum, category) => sum + category.questions.length, 0),
        categories: schema.metadata.categories || schema.categories.length,
        active: true,
        archived: false,
      },
    },
  ];

  schema.categories.forEach((category, categoryIndex) => {
    documents.push({
      collection: 'assessmentNodes',
      id: `${assessmentId}__category__${category.id}`,
      data: {
        assessmentId,
        version,
        type: 'category',
        parentId: null,
        key: category.id,
        title: category.title,
        order: categoryIndex + 1,
        active: true,
      },
    });

    const grouped = new Map<string, SchemaQuestion[]>();
    category.questions.forEach((question) => {
      const section = sectionName(question);
      if (!grouped.has(section)) grouped.set(section, []);
      grouped.get(section)?.push(question);
    });

    Array.from(grouped.entries()).forEach(([section, questions], sectionIndex) => {
      const subcategoryKey = slugify(section);
      documents.push({
        collection: 'assessmentNodes',
        id: `${assessmentId}__subcategory__${category.id}__${subcategoryKey}`,
        data: {
          assessmentId,
          version,
          type: 'subcategory',
          parentId: category.id,
          key: subcategoryKey,
          title: section,
          order: sectionIndex + 1,
          active: true,
        },
      });

      questions.forEach((question, questionIndex) => {
        const level = inferSeverity(question);
        documents.push({
          collection: 'assessmentNodes',
          id: `${assessmentId}__question__${question.id}`,
          data: {
            assessmentId,
            version,
            type: 'question',
            parentId: subcategoryKey,
            key: question.id,
            text: question.text,
            order: questionIndex + 1,
            active: true,
            questionType: question.type,
            required: Boolean(question.required),
            hasCommentField: Boolean(question.hasCommentField),
            commentPrompt: question.commentPrompt || 'Add additional details (optional).',
            polarityValidated: Boolean(question.polarityValidated),
            polarityFixed: Boolean(question.polarityFixed),
            answerMeaningYes: question.answerMeaning?.yes || 'Compliant / Secure',
            answerMeaningNo: question.answerMeaning?.no || 'Deficient / Non-Compliant',
            manualReview: Boolean(question.manualReview),
            manualReviewReason: question.manualReviewReason || null,
            tags: Array.isArray(question.tags) ? question.tags : [],
            severityLevel: level,
            severityWeight:
              typeof question.severityWeight === 'number'
                ? question.severityWeight
                : severityWeight(level),
          },
        });
      });
    });
  });

  return {
    assessmentId,
    version,
    documents,
  };
}

function normalizeBundle(input: any): ImportBundle {
  if (Array.isArray(input?.documents)) {
    return {
      assessmentId: String(input.assessmentId || ''),
      version: String(input.version || ''),
      documents: input.documents,
    };
  }

  if (Array.isArray(input?.categories) && input?.id && input?.version) {
    return buildBundleFromSchema(input as AssessmentSchema);
  }

  throw new Error('Unsupported file. Use either the full schema JSON or the generated Firestore bundle JSON.');
}

export const AdminAssessmentImport: React.FC = () => {
  const { user } = useAuth();
  const [bundle, setBundle] = useState<ImportBundle | null>(null);
  const [fileName, setFileName] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const canImport = useMemo(
    () => Boolean(user && (user.roles.includes('admin') || user.roles.includes('owner'))),
    [user]
  );

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setError(null);
    setStatus(null);
    setBundle(null);
    setFileName('');

    if (!file) return;

    try {
      const raw = await file.text();
      const parsed = JSON.parse(raw);
      const nextBundle = normalizeBundle(parsed);
      setBundle(nextBundle);
      setFileName(file.name);
      setStatus(
        `Loaded ${file.name}. Ready to import ${nextBundle.documents.length} documents for ${nextBundle.assessmentId} v${nextBundle.version}.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the selected file.');
    }
  };

  const handleImport = async () => {
    if (!bundle) {
      setError('Load a schema or bundle JSON file first.');
      return;
    }
    if (!canImport) {
      setError('Your account does not have the admin or owner role required to import assessment definitions.');
      return;
    }

    setIsImporting(true);
    setError(null);
    setStatus(`Starting import for ${bundle.assessmentId}...`);

    try {
      const db = getFirebaseDb();
      let batch = writeBatch(db);
      let ops = 0;
      let committed = 0;

      for (const documentDef of bundle.documents) {
        const ref = doc(db, documentDef.collection, documentDef.id);
        batch.set(
          ref,
          {
            ...documentDef.data,
            updatedAt: serverTimestamp(),
            ...(documentDef.collection === 'assessments' ? { createdAt: serverTimestamp() } : {}),
          },
          { merge: true }
        );
        ops += 1;

        if (ops === 400) {
          await batch.commit();
          committed += ops;
          batch = writeBatch(db);
          ops = 0;
          setStatus(`Imported ${committed} of ${bundle.documents.length} documents...`);
        }
      }

      if (ops > 0) {
        await batch.commit();
        committed += ops;
      }

      setStatus(`Import complete. Wrote ${committed} documents for ${bundle.assessmentId} v${bundle.version}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <AppShell title="Assessment Import" isDashboard={true}>
      <div className="page-shell">
        <section className="page-card" style={{ maxWidth: 880 }}>
          <div className="page-card__header">
            <div>
              <p className="page-kicker">Admin Tools</p>
              <h1 className="page-title">Assessment Import</h1>
              <p className="page-subtitle">
                Load the revised schema file or the generated Firestore bundle and import it with your signed-in Firebase session.
              </p>
            </div>
          </div>

          <div className="page-card__body">
            <p><strong>Signed in as:</strong> {user?.email || 'Unknown user'}</p>
            <p><strong>Roles:</strong> {user?.roles?.join(', ') || 'none'}</p>
            <p><strong>Status:</strong> {canImport ? 'Import permitted' : 'Admin or owner role required'}</p>

            <label className="input-label" htmlFor="assessment-import-file">Schema or bundle JSON</label>
            <input
              id="assessment-import-file"
              type="file"
              accept=".json,application/json"
              onChange={handleFileChange}
              disabled={isImporting}
            />

            {fileName && <p><strong>Loaded file:</strong> {fileName}</p>}
            {bundle && (
              <div style={{ marginTop: '1rem' }}>
                <p><strong>Assessment:</strong> {bundle.assessmentId}</p>
                <p><strong>Version:</strong> {bundle.version}</p>
                <p><strong>Documents:</strong> {bundle.documents.length}</p>
              </div>
            )}

            {status && (
              <div className="notice notice--success" style={{ marginTop: '1rem' }}>
                {status}
              </div>
            )}
            {error && (
              <div className="notice notice--error" style={{ marginTop: '1rem' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
              <button
                type="button"
                className="report-action report-action--primary"
                onClick={() => void handleImport()}
                disabled={!bundle || !canImport || isImporting}
              >
                {isImporting ? 'Importing...' : 'Import Assessment Definition'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </AppShell>
  );
};

export default AdminAssessmentImport;
